import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { HttpErrorResponse } from "@angular/common/http";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { RoleService } from "@app/shared/services/roles.service";
import { WorkflowDraftConfirmationService } from "../workflow-registry-diagnostics/workflow-draft-confirmation.service";
import { DataResetResult, PlatformDataResetAdminService } from "./platform-data-reset-admin.service";

const PLATFORM_ACCESS_ADMIN_DATA_RESET = "platform_access_admin.data_reset";

export interface DataResetDisplayError {
  title: string;
  detail: string | null;
}

@Component({
    selector: "phms-platform-data-reset-admin",
    imports: [CommonModule, UiLibraryModule],
    templateUrl: "./platform-data-reset-admin.component.html",
    styleUrl: "./platform-data-reset-admin.component.scss"
})
export class PlatformDataResetAdminComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataResetAdminService = inject(PlatformDataResetAdminService);
  private readonly confirmationService = inject(WorkflowDraftConfirmationService);
  private readonly roles = inject(RoleService);

  readonly canReset = this.roles.hasAnyPermissionSignal(PLATFORM_ACCESS_ADMIN_DATA_RESET);

  readonly busy = signal(false);
  readonly result = signal<DataResetResult | null>(null);
  readonly errorState = signal<DataResetDisplayError | null>(null);

  async confirmAndReset(): Promise<void> {
    if (this.busy() || !this.canReset()) {
      return;
    }

    const confirmed = await this.confirmationService.confirm({
      heading: "Reset all demo data?",
      message: "This action is destructive and irreversible. It will wipe all user-entered hearing data — every "
        + "hearing, schedule, assignment, decision, SDMF analysis, note, and imported inmate. Documents are kept. "
        + "The 15-inmate demo baseline is restored, centered on today. Logins, feature flags, and terminology "
        + "are preserved.",
      confirmText: "Reset all data",
      cancelText: "Keep current data",
    });

    if (!confirmed) {
      return;
    }

    this.result.set(null);
    this.errorState.set(null);
    this.busy.set(true);

    this.dataResetAdminService
      .resetDemoData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.busy.set(false);
          this.result.set(result);
        },
        error: (error) => {
          this.busy.set(false);
          this.errorState.set(mapDataResetError(error));
        },
      });
  }
}

function mapDataResetError(error: unknown): DataResetDisplayError {
  if (error instanceof HttpErrorResponse) {
    const problem = (error.error ?? null) as { title?: unknown; detail?: unknown } | string | null;
    if (problem && typeof problem === "object") {
      const title = typeof problem.title === "string" && problem.title.trim().length > 0
        ? problem.title
        : null;
      const detail = typeof problem.detail === "string" && problem.detail.trim().length > 0
        ? problem.detail
        : null;

      if (title || detail) {
        return {
          title: title ?? "The data reset failed.",
          detail,
        };
      }
    }

    return {
      title: "The data reset failed.",
      detail: error.message ?? null,
    };
  }

  return {
    title: "The data reset failed.",
    detail: null,
  };
}
